import { useState, useMemo, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { CaretLeft, CaretRight, Warning, Wrench, MapPin, Clock, Plus, User } from "@phosphor-icons/react";
import { computeRowBars, BAR_HEIGHT_PX } from "../lib/calendarLayout";
import { fmtDate as ukDate } from "../lib/dateUtil";

const STATUS_COLORS = {
  scheduled: { stripe: "#3b82f6" },
  in_progress: { stripe: "#f59e0b" },
  completed: { stripe: "#10b981" },
  cancelled: { stripe: "#f87171" },
};
const STATUS_LABELS = { scheduled: "Scheduled", in_progress: "In Progress", completed: "Completed", cancelled: "Cancelled" };
const DAYS_FULL = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const HOURS = Array.from({ length: 15 }, (_, i) => i + 6); // 06:00-20:00
const START_HOUR = 6;
const HOUR_H = 60;

function getWeekStart(date) {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() - day + (day === 0 ? -6 : 1));
  return d;
}
function fmtDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function fmtShort(d) { return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" }); }
function timeToMin(t) { if (!t) return null; const [h, m] = t.split(":").map(Number); return h * 60 + (m || 0); }

export function JobWeekView({ jobs = [], onReschedule, onQuickCreate }) {
  const today = new Date();
  const [weekStart, setWeekStart] = useState(() => getWeekStart(today));
  const [vendorFilter, setVendorFilter] = useState("all");
  const [dragJob, setDragJob] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const gridRef = useRef(null);
  const containerRef = useRef(null);

  const weekDates = useMemo(() => Array.from({ length: 7 }, (_, i) => { const d = new Date(weekStart); d.setDate(d.getDate() + i); return d; }), [weekStart]);
  const todayStr = fmtDate(today);
  const weekEnd = weekDates[6];

  const filteredJobs = useMemo(() => {
    if (vendorFilter === "all") return jobs;
    return jobs.filter(j => j.assigned_vendor_id === vendorFilter);
  }, [jobs, vendorFilter]);

  const jobsByDate = useMemo(() => {
    const map = {};
    weekDates.forEach(d => { map[fmtDate(d)] = []; });
    filteredJobs.forEach(j => { const ds = j.schedule?.start_date; if (ds && map[ds] !== undefined) map[ds].push(j); });
    return map;
  }, [filteredJobs, weekDates]);

  // Multi-day jobs render as continuous banner bars at the top of the week
  // (the "all-day" strip) instead of as time-block columns in the hour grid.
  // This is the standard calendar UX for jobs that span more than one day —
  // a single bar across all the days the job covers, with ‹/› continuation
  // arrows when the booking extends beyond this week.
  const rowDays = useMemo(() => weekDates.map(fmtDate), [weekDates]);
  const bannerBars = useMemo(() => {
    const events = filteredJobs
      .filter((j) => j.schedule?.start_date && j.schedule?.end_date && j.schedule.end_date !== j.schedule.start_date)
      .map((j) => ({
        id: j.id,
        startISO: j.schedule.start_date,
        endISO: j.schedule.end_date,
        startTime: j.schedule.start_time || "",
        job: j,
      }));
    return computeRowBars(rowDays, events);
  }, [filteredJobs, rowDays]);

  const conflicts = useMemo(() => {
    const set = new Set();
    Object.values(jobsByDate).forEach(dateJobs => {
      for (let i = 0; i < dateJobs.length; i++) {
        for (let k = i + 1; k < dateJobs.length; k++) {
          const a = dateJobs[i], b = dateJobs[k];
          if (a.assigned_vendor_id && a.assigned_vendor_id === b.assigned_vendor_id) {
            const aS = a.schedule?.start_time || "00:00", aE = a.schedule?.end_time || "23:59";
            const bS = b.schedule?.start_time || "00:00", bE = b.schedule?.end_time || "23:59";
            if (aS < bE && bS < aE) { set.add(a.id); set.add(b.id); }
          }
        }
      }
    });
    return set;
  }, [jobsByDate]);

  const prevWeek = () => setWeekStart(prev => { const d = new Date(prev); d.setDate(d.getDate() - 7); return d; });
  const nextWeek = () => setWeekStart(prev => { const d = new Date(prev); d.setDate(d.getDate() + 7); return d; });
  const goToday = () => setWeekStart(getWeekStart(today));

  // Auto-scroll to current time on mount and when returning to "today" week
  useEffect(() => {
    if (!gridRef.current) return;
    const now = new Date();
    const mins = now.getHours() * 60 + now.getMinutes();
    const scrollTop = Math.max(0, ((mins - START_HOUR * 60) / 60) * HOUR_H - 120);
    gridRef.current.scrollTo({ top: scrollTop, behavior: "smooth" });
  }, [weekStart]);

  // Keyboard nav
  useEffect(() => {
    const handler = (e) => {
      if (!containerRef.current?.contains(document.activeElement) && document.activeElement !== document.body) return;
      if (e.key === "ArrowLeft") { e.preventDefault(); prevWeek(); }
      if (e.key === "ArrowRight") { e.preventDefault(); nextWeek(); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  });

  const assignedVendors = useMemo(() => {
    const seen = new Map();
    jobs.forEach(j => { if (j.assigned_vendor_id && j.assigned_vendor_name && !seen.has(j.assigned_vendor_id)) seen.set(j.assigned_vendor_id, j.assigned_vendor_name); });
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [jobs]);

  // Drag
  const handleDragStart = (e, job) => { setDragJob(job); e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", job.id); e.currentTarget.style.opacity = "0.4"; };
  const handleDragEnd = (e) => { e.currentTarget.style.opacity = "1"; setDragJob(null); setDropTarget(null); };
  const handleDragOver = (e, ds) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDropTarget(ds); };
  const handleDragLeave = () => setDropTarget(null);
  const handleDrop = (e, ds) => {
    e.preventDefault(); setDropTarget(null);
    if (!dragJob || !onReschedule) return;
    if (dragJob.schedule?.start_date === ds) return;
    onReschedule(dragJob.id, ds);
    setDragJob(null);
  };

  // Week totals
  const weekJobCount = Object.values(jobsByDate).reduce((s, arr) => s + arr.length, 0);
  const conflictCount = conflicts.size;

  return (
    <TooltipProvider delayDuration={200}>
      <div data-testid="job-week-view" ref={containerRef} tabIndex={-1} className="outline-none">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={prevWeek} data-testid="week-prev"><CaretLeft size={14} /></Button>
            <h2 className="text-lg font-bold font-[Chivo] min-w-[260px] text-center">
              {fmtShort(weekDates[0])} — {fmtShort(weekEnd)}
            </h2>
            <Button variant="outline" size="sm" onClick={nextWeek} data-testid="week-next"><CaretRight size={14} /></Button>
            <Button variant="ghost" size="sm" className="text-xs" onClick={goToday} data-testid="week-today">Today</Button>
          </div>
          <div className="flex items-center gap-3">
            <Select value={vendorFilter} onValueChange={setVendorFilter}>
              <SelectTrigger className="w-[180px] h-8 text-xs" data-testid="week-vendor-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Suppliers</SelectItem>
                {assignedVendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="hidden sm:flex items-center gap-3 text-[10px]">
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <span key={k} className="flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ backgroundColor: STATUS_COLORS[k].stripe }} />{v}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-4 mb-3 text-xs text-muted-foreground">
          <span>{weekJobCount} job{weekJobCount !== 1 ? "s" : ""} this week</span>
          {conflictCount > 0 && (
            <span className="flex items-center gap-1 text-red-600 dark:text-red-400">
              <Warning size={12} weight="fill" /> {Math.floor(conflictCount / 2)} conflict{conflictCount > 2 ? "s" : ""}
            </span>
          )}
        </div>

        <div className="border border-border rounded-lg overflow-hidden bg-card" data-testid="week-grid">
          {/* Day headers with job counts */}
          <div className="grid grid-cols-[56px_repeat(7,1fr)] border-b border-border">
            <div className="bg-muted/40 border-r border-border" />
            {weekDates.map((d, i) => {
              const ds = fmtDate(d);
              const isToday = ds === todayStr;
              const count = (jobsByDate[ds] || []).length;
              return (
                <div
                  key={i}
                  className={`py-2 px-1 text-center border-r border-border last:border-r-0 relative group ${isToday ? "bg-primary/10" : "bg-muted/40"}`}
                >
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{DAYS_FULL[i].slice(0, 3)}</p>
                  <div className="flex items-center justify-center gap-1 mt-0.5">
                    <p className={`text-sm font-bold ${isToday ? "bg-primary text-primary-foreground rounded-full h-6 w-6 flex items-center justify-center" : ""}`}>
                      {d.getDate()}
                    </p>
                    {count > 0 && <span className="text-[8px] bg-muted text-muted-foreground rounded-full px-1.5 leading-[14px] font-semibold">{count}</span>}
                  </div>
                  {/* Quick add in header */}
                  {onQuickCreate && (
                    <button
                      onClick={() => onQuickCreate(ds)}
                      className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity h-4 w-4 rounded-full bg-primary/10 hover:bg-primary/20 flex items-center justify-center"
                      data-testid={`week-quick-add-${ds}`}
                    >
                      <Plus size={10} className="text-primary" weight="bold" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* All-day strip — shows BOTH:
              1. Single-day jobs without a start_time (existing behaviour)
              2. Multi-day jobs as continuous banner bars across all the
                 days they span (Google Calendar style). Bars stay in the
                 same vertical slot across days and use ‹/› arrows when the
                 booking extends beyond this week. */}
          {(() => {
            const allDayJobs = weekDates.map(d => (jobsByDate[fmtDate(d)] || []).filter(j => !j.schedule?.start_time && (!j.schedule?.end_date || j.schedule.end_date === j.schedule.start_date)));
            const hasSingleDay = allDayJobs.some(arr => arr.length > 0);
            const hasMultiDay = bannerBars.bars.length > 0;
            if (!hasSingleDay && !hasMultiDay) return null;
            const slots = Math.max(1, bannerBars.maxSlot + 1);
            const bannerHeight = slots * (BAR_HEIGHT_PX + 2) + 6;
            return (
              <div className="border-b border-border">
                {/* Multi-day banner bars (continuous across days) */}
                {hasMultiDay && (
                  <div
                    className="grid grid-cols-[56px_repeat(7,1fr)] relative"
                    style={{ minHeight: `${bannerHeight}px` }}
                  >
                    <div className="bg-muted/40 border-r border-border flex items-start justify-end pr-2 pt-1">
                      <span className="text-[9px] text-muted-foreground">all day</span>
                    </div>
                    {/* Background day columns so dragging onto a column still
                       triggers handleDrop with the correct date. */}
                    {weekDates.map((d, i) => {
                      const ds = fmtDate(d);
                      return (
                        <div
                          key={i}
                          className={`border-r border-border last:border-r-0 ${dropTarget === ds ? "bg-primary/10" : ""}`}
                          onDragOver={e => handleDragOver(e, ds)}
                          onDragLeave={handleDragLeave}
                          onDrop={e => handleDrop(e, ds)}
                        />
                      );
                    })}
                    {/* Continuous bars overlaid on the 8-column grid (col 1
                       = time gutter, cols 2..8 = the 7 day columns). */}
                    {bannerBars.bars.map((b) => {
                      const j = b.event.job;
                      const sc = STATUS_COLORS[j.status] || STATUS_COLORS.scheduled;
                      const isConflict = conflicts.has(j.id);
                      return (
                        <Tooltip key={j.id}>
                          <TooltipTrigger asChild>
                            <Link
                              to={`/jobs/${j.id}`}
                              draggable
                              onDragStart={e => handleDragStart(e, j)}
                              onDragEnd={handleDragEnd}
                              className={`flex items-center gap-1 px-2 rounded text-[10px] text-white font-medium cursor-grab active:cursor-grabbing hover:brightness-110 transition ${isConflict ? "ring-2 ring-red-500 ring-offset-1" : ""}`}
                              style={{
                                // +1 to skip the leading 56px time-gutter column.
                                gridColumn: `${b.startCol + 1} / span ${b.span}`,
                                gridRow: 1,
                                height: `${BAR_HEIGHT_PX - 2}px`,
                                marginTop: `${b.slot * (BAR_HEIGHT_PX + 2) + 2}px`,
                                marginLeft: b.continuesLeft ? 0 : 3,
                                marginRight: b.continuesRight ? 0 : 3,
                                backgroundColor: sc.stripe,
                                alignSelf: "start",
                                position: "relative",
                                zIndex: 5,
                              }}
                              data-testid={`week-job-${j.id}-bar`}
                            >
                              {b.continuesLeft && <span className="opacity-80 shrink-0">‹</span>}
                              {j.schedule?.start_time && <span className="opacity-90 shrink-0">{j.schedule.start_time}</span>}
                              <span className="truncate flex-1">{j.title}</span>
                              {b.continuesRight && <span className="opacity-80 shrink-0">›</span>}
                            </Link>
                          </TooltipTrigger>
                          <TooltipContent side="bottom" className="max-w-[240px] p-3">
                            <p className="font-semibold text-xs mb-1">{j.title}</p>
                            <p className="text-[10px] text-muted-foreground font-mono mb-1">{j.reference}</p>
                            {j.assigned_vendor_name && <p className="text-[10px] flex items-center gap-1 mb-0.5"><Wrench size={10} /> {j.assigned_vendor_name}</p>}
                            {j.assigned_operative_name && <p className="text-[10px] flex items-center gap-1 mb-0.5 text-primary"><User size={10} /> {j.assigned_operative_name}</p>}
                            {(j.street_address || j.town_city) && <p className="text-[10px] flex items-center gap-1 mb-0.5 truncate"><MapPin size={10} /> {[j.street_address, j.town_city].filter(Boolean).join(", ")}</p>}
                            {j.schedule?.start_time && <p className="text-[10px] flex items-center gap-1"><Clock size={10} /> {j.schedule.start_time}{j.schedule.end_time ? ` — ${j.schedule.end_time}` : ""}</p>}
                            <p className="text-[10px] flex items-center gap-1 text-primary mt-0.5"><Clock size={10} /> Multi-day: {ukDate(j.schedule.start_date)} → {ukDate(j.schedule.end_date)}</p>
                            {isConflict && <p className="text-[10px] text-red-600 dark:text-red-400 mt-1 flex items-center gap-1 font-medium"><Warning size={10} weight="fill" /> Time conflict</p>}
                            <p className="text-[9px] text-muted-foreground mt-1.5 italic">Drag the bar to reschedule</p>
                          </TooltipContent>
                        </Tooltip>
                      );
                    })}
                  </div>
                )}
                {/* Single-day all-day chips (legacy strip) */}
                {hasSingleDay && (
                  <div className="grid grid-cols-[56px_repeat(7,1fr)]">
                    <div className="bg-muted/40 border-r border-border flex items-center justify-end pr-2">
                      <span className="text-[9px] text-muted-foreground">{hasMultiDay ? "" : "all day"}</span>
                    </div>
                    {weekDates.map((d, i) => {
                      const ds = fmtDate(d);
                      const dayAllDay = allDayJobs[i];
                      return (
                        <div
                          key={i}
                          className={`px-0.5 py-1 border-r border-border last:border-r-0 space-y-0.5 min-h-[28px] ${dropTarget === ds ? "bg-primary/10" : ""}`}
                          onDragOver={e => handleDragOver(e, ds)}
                          onDragLeave={handleDragLeave}
                          onDrop={e => handleDrop(e, ds)}
                        >
                          {dayAllDay.map(j => {
                            const sc = STATUS_COLORS[j.status] || STATUS_COLORS.scheduled;
                            return (
                              <Link
                                key={j.id}
                                to={`/jobs/${j.id}`}
                                draggable
                                onDragStart={e => handleDragStart(e, j)}
                                onDragEnd={handleDragEnd}
                                className="block rounded px-1.5 py-0.5 text-[9px] text-white truncate font-medium cursor-grab active:cursor-grabbing hover:brightness-110"
                                style={{ backgroundColor: sc.stripe }}
                                data-testid={`week-allday-${j.id}`}
                              >
                                {j.title}
                              </Link>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Time grid */}
          <div className="grid grid-cols-[56px_repeat(7,1fr)] overflow-y-auto" style={{ maxHeight: "540px" }} ref={gridRef}>
            {/* Time labels */}
            <div className="relative border-r border-border">
              {HOURS.map(h => (
                <div key={h} className="flex items-start justify-end pr-2 text-[10px] text-muted-foreground" style={{ height: `${HOUR_H}px` }}>
                  <span className="-mt-1.5">{String(h).padStart(2, "0")}:00</span>
                </div>
              ))}
            </div>

            {/* Day columns */}
            {weekDates.map((d, colIdx) => {
              const ds = fmtDate(d);
              const isToday = ds === todayStr;
              // Time-grid blocks: only show single-day jobs with a start_time.
              // Multi-day jobs are rendered as banners in the all-day strip above.
              const dayJobs = (jobsByDate[ds] || []).filter(j =>
                j.schedule?.start_time
                && (!j.schedule?.end_date || j.schedule.end_date === j.schedule.start_date)
              );

              // Detect overlapping blocks for side-by-side rendering
              const blocks = dayJobs.map(j => {
                const sMin = timeToMin(j.schedule?.start_time);
                const eMin = timeToMin(j.schedule?.end_time) || (sMin + 60);
                return { job: j, sMin, eMin };
              }).sort((a, b) => a.sMin - b.sMin);

              // Simple overlap grouping
              const groups = [];
              blocks.forEach(blk => {
                const existing = groups.find(g => g.some(b => b.sMin < blk.eMin && blk.sMin < b.eMin));
                if (existing) existing.push(blk);
                else groups.push([blk]);
              });

              const posMap = new Map();
              groups.forEach(g => {
                g.forEach((blk, idx) => { posMap.set(blk.job.id, { col: idx, total: g.length }); });
              });

              return (
                <div
                  key={colIdx}
                  className={`relative border-r border-border last:border-r-0 transition-colors
                    ${isToday ? "bg-primary/[0.03]" : ""}
                    ${dropTarget === ds ? "bg-primary/10" : ""}
                  `}
                  onDragOver={e => handleDragOver(e, ds)}
                  onDragLeave={handleDragLeave}
                  onDrop={e => handleDrop(e, ds)}
                  data-testid={`week-col-${ds}`}
                >
                  {/* Hour lines */}
                  {HOURS.map(h => (
                    <div key={h} className="border-b border-border/40" style={{ height: `${HOUR_H}px` }}>
                      {/* Half-hour dashed line */}
                      <div className="border-b border-dashed border-border/20 h-1/2" />
                    </div>
                  ))}

                  {/* Current time indicator */}
                  {isToday && (() => {
                    const now = new Date();
                    const mins = now.getHours() * 60 + now.getMinutes();
                    const top = ((mins - START_HOUR * 60) / 60) * HOUR_H;
                    if (top < 0 || top > HOURS.length * HOUR_H) return null;
                    return (
                      <div className="absolute left-0 right-0 z-20 pointer-events-none" style={{ top: `${top}px` }} data-testid="week-time-indicator">
                        <div className="h-0.5 bg-red-500 w-full shadow-sm" />
                        <div className="absolute -left-1 -top-[3px] h-2 w-2 rounded-full bg-red-500 shadow" />
                      </div>
                    );
                  })()}

                  {/* Job blocks — side by side for overlaps */}
                  {dayJobs.map(j => {
                    const sMin = timeToMin(j.schedule?.start_time);
                    const eMin = timeToMin(j.schedule?.end_time);
                    if (sMin === null) return null;
                    const top = ((sMin - START_HOUR * 60) / 60) * HOUR_H;
                    const height = eMin !== null ? ((eMin - sMin) / 60) * HOUR_H : HOUR_H;
                    const sc = STATUS_COLORS[j.status] || STATUS_COLORS.scheduled;
                    const isConflict = conflicts.has(j.id);
                    const pos = posMap.get(j.id) || { col: 0, total: 1 };
                    const widthPct = 100 / pos.total;
                    const leftPct = pos.col * widthPct;

                    return (
                      <Tooltip key={j.id}>
                        <TooltipTrigger asChild>
                          <Link
                            to={`/jobs/${j.id}`}
                            draggable
                            onDragStart={e => handleDragStart(e, j)}
                            onDragEnd={handleDragEnd}
                            className={`absolute rounded-md px-1.5 py-1 overflow-hidden cursor-grab active:cursor-grabbing transition-shadow hover:shadow-lg hover:brightness-110 z-10 ${isConflict ? "ring-2 ring-red-500" : ""}`}
                            style={{
                              top: `${Math.max(0, top)}px`,
                              height: `${Math.max(22, height - 2)}px`,
                              left: `calc(${leftPct}% + 2px)`,
                              width: `calc(${widthPct}% - 4px)`,
                              backgroundColor: sc.stripe,
                              minHeight: "22px",
                            }}
                            data-testid={`week-job-${j.id}`}
                          >
                            <p className="text-[9px] font-bold text-white truncate leading-tight">{j.title}</p>
                            {height > 32 && <p className="text-[8px] text-white/80 truncate">{j.schedule?.start_time}{j.schedule?.end_time ? ` — ${j.schedule.end_time}` : ""}</p>}
                            {height > 48 && j.assigned_vendor_name && <p className="text-[8px] text-white/70 truncate flex items-center gap-0.5"><Wrench size={8} /> {j.assigned_vendor_name}</p>}
                            {height > 62 && (j.street_address || j.town_city) && <p className="text-[7px] text-white/60 truncate flex items-center gap-0.5"><MapPin size={7} /> {[j.street_address, j.town_city].filter(Boolean).join(", ")}</p>}
                          </Link>
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-[220px] p-3">
                          <p className="font-semibold text-xs mb-1">{j.title}</p>
                          <p className="text-[10px] text-muted-foreground font-mono mb-1">{j.reference}</p>
                          {j.assigned_vendor_name && <p className="text-[10px] flex items-center gap-1 mb-0.5"><Wrench size={10} /> {j.assigned_vendor_name}</p>}
                          {j.assigned_operative_name && <p className="text-[10px] flex items-center gap-1 mb-0.5 text-primary"><User size={10} /> {j.assigned_operative_name}</p>}
                          {(j.street_address || j.town_city) && <p className="text-[10px] flex items-center gap-1 mb-0.5 truncate"><MapPin size={10} /> {[j.street_address, j.town_city].filter(Boolean).join(", ")}</p>}
                          {j.schedule?.start_time && <p className="text-[10px] flex items-center gap-1"><Clock size={10} /> {j.schedule.start_time}{j.schedule.end_time ? ` — ${j.schedule.end_time}` : ""}</p>}
                          {isConflict && <p className="text-[10px] text-red-600 mt-1 flex items-center gap-1 font-medium"><Warning size={10} weight="fill" /> Scheduling conflict</p>}
                          <p className="text-[9px] text-muted-foreground mt-1.5 italic">Drag to reschedule</p>
                        </TooltipContent>
                      </Tooltip>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

        {/* Hint */}
        <p className="text-[10px] text-muted-foreground mt-2 text-center">
          Drag jobs to reschedule · Arrow keys to navigate weeks · Click <Plus size={8} className="inline" weight="bold" /> to quick-add
        </p>
      </div>
    </TooltipProvider>
  );
}
